import { Table, TableCell, TableHead, TableRow } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { CSSProperties } from "@material-ui/styles";
import {
  ATPCTPListItemsFieldPolicy,
  RequireAttentionATPCTPRequestResultTypeFieldPolicy
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import classNames from "classnames";
import moment from "moment-timezone/moment-timezone-utils";
import React, { useRef } from "react";
import { useIntl } from "react-intl";

import MyTypography from "../../../../components/MyTypography";
import StickyHorizontalScrollBar from "../../../../components/StickyHorizontalScrollBar";
import TableCellHeader from "../../../../components/TableCellHeader";

const freezeCell: CSSProperties = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  minWidth: "8rem",
  verticalAlign: "text-top",
  padding: "1.7rem 1.2rem !important",
  borderRight: "1px solid #E0E0E0",
  boxShadow: "4px 0px 8px rgba(0, 0, 0, 0.05)"
};

const useStyles = makeStyles(
  theme => ({
    titleTable: {
      marginTop: "40px"
    },
    root: {
      marginTop: "24px",
      overflowX: "auto",
      width: "100%",
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: "2px",
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      },
      "& .MuiTableCell-head": {
        background: "#DAF7FB",
        padding: "2.8rem 1.2rem",
        "& .topRow": {
          background: "#FDF2D0 !important"
        }
      }
    },
    colFreeze: {
      background: "#EEEEEE !important",
      ...freezeCell
    },
    colFreeze2: {
      background: "#DAF7FB !important",
      ...freezeCell
    },
    col120: {
      background: "#EEEEEE !important",
      minWidth: "120px"
    },
    col160: {
      background: "#EEEEEE !important",
      minWidth: "160px"
    },
    col80: {
      background: "#EEEEEE !important",
      minWidth: "80px"
    },
    col140: {
      background: "#EEEEEE !important",
      minWidth: "140px"
    },
    col_80: {
      minWidth: "80px"
    },
    col_140: {
      minWidth: "140px"
    },
    rowData: {
      "& th": {
        padding: "1.7rem 1.2rem !important",
        background: "#FFF"
      }
    },
    orderNo: {
      color: theme.palette.info.main
    },
    flex: { display: "flex", alignItems: "center" },
    topRow: {
      background: "#FDF2D0 !important"
    },
    bottomRow: {
      background: "#FFFFFF !important"
    }
  }),
  { name: "AltMatTable" }
);

export interface IAptCtpTableDataProps {
  onClickCheckBox: (id) => void;
  selectedData: boolean;
  displayOrderNo?: boolean;
  data: RequireAttentionATPCTPRequestResultTypeFieldPolicy;
}

const AtpCtpTableData: React.FC<IAptCtpTableDataProps> = ({
  data,
  displayOrderNo
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const convertDate = date => {
    const resultDate = moment(date).format("DD/MM/YYYY");
    return resultDate === "Invalid date" ? "-" : resultDate;
  };

  return (
    <div
      className={classes.root}
      ref={containerRef}
      data-testid="atpCtpTableData"
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCellHeader
              className={classes.colFreeze}
              rowSpan={2}
              textAlign={"left"}
            >
              <MyTypography variant={"subtitle3"}>
                {intl.formatMessage({ defaultMessage: "Original" })}
              </MyTypography>
            </TableCellHeader>
            {displayOrderNo ? (
              <TableCellHeader className={classes.col120}>
                {intl.formatMessage({ defaultMessage: "Order No." })}
              </TableCellHeader>
            ) : null}
            <TableCellHeader className={classes.col160}>
              {intl.formatMessage({ defaultMessage: "Item" })}
            </TableCellHeader>
            <TableCellHeader className={classes.col120}>
              {intl.formatMessage({ defaultMessage: "Material Code" })}
            </TableCellHeader>
            <TableCellHeader className={classes.col160}>
              {intl.formatMessage({ defaultMessage: "Material Description" })}
            </TableCellHeader>
            <TableCellHeader className={classes.col80}>
              {intl.formatMessage({ defaultMessage: "QTY" })}
            </TableCellHeader>
            <TableCellHeader className={classes.col120}>
              {intl.formatMessage({ defaultMessage: "Request Date" })}
            </TableCellHeader>
            <TableCellHeader className={classes.col80} colSpan={5}>
              {intl.formatMessage({ defaultMessage: "Plant" })}
            </TableCellHeader>
          </TableRow>
          <TableRow className={classNames(classes.rowData)}>
            {displayOrderNo ? (
              <TableCell className={classes.topRow}>
                <MyTypography variant={"body3"}>
                  {data ? data?.orderNo || "-" : <Skeleton />}
                </MyTypography>
              </TableCell>
            ) : null}
            <TableCell className={classes.topRow}>
              <MyTypography variant="body3">
                {data ? data?.originalItemNo || "-" : <Skeleton />}
              </MyTypography>
            </TableCell>
            <TableCell className={classes.topRow}>
              <MyTypography variant="body3">
                {data ? data?.materialVariantCode || "-" : <Skeleton />}
              </MyTypography>
            </TableCell>
            <TableCell className={classes.topRow}>
              <MyTypography variant="body3">
                {data ? data?.materialVariantDescription || "-" : <Skeleton />}
              </MyTypography>
            </TableCell>
            <TableCell className={classes.topRow}>
              <MyTypography variant="body3">
                {data ? data?.originalQuantity || "0" : <Skeleton />}
              </MyTypography>
            </TableCell>
            <TableCell className={classes.topRow}>
              <MyTypography variant="body3">
                {data ? (
                  data?.originalRequestDate ? (
                    convertDate(data?.originalRequestDate)
                  ) : (
                    "-"
                  )
                ) : (
                  <Skeleton />
                )}
              </MyTypography>
            </TableCell>
            <TableCell className={classes.topRow} colSpan={5}>
              <MyTypography variant="body3">
                {data ? data?.originalPlant || "-" : <Skeleton />}
              </MyTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCellHeader
              className={classes.colFreeze2}
              rowSpan={
                ((data?.listItems ?? []) as ATPCTPListItemsFieldPolicy[])
                  .length + 1
              }
              textAlign={"left"}
            >
              <MyTypography variant={"subtitle3"}>
                {intl.formatMessage({ defaultMessage: "iPlan" })}
              </MyTypography>
            </TableCellHeader>
            {displayOrderNo ? (
              <TableCellHeader>
                {intl.formatMessage({ defaultMessage: "Order No." })}
              </TableCellHeader>
            ) : null}
            <TableCellHeader>
              {intl.formatMessage({ defaultMessage: "Item" })}
            </TableCellHeader>
            <TableCellHeader>
              {intl.formatMessage({ defaultMessage: "Material Code" })}
            </TableCellHeader>
            <TableCellHeader>
              {intl.formatMessage({ defaultMessage: "Material Description" })}
            </TableCellHeader>
            <TableCellHeader>
              {intl.formatMessage({ defaultMessage: "iPlan Conf. QTY" })}
            </TableCellHeader>
            <TableCellHeader>
              {intl.formatMessage({ defaultMessage: "Confirm Date" })}
            </TableCellHeader>
            <TableCellHeader>
              {intl.formatMessage({ defaultMessage: "Plant" })}
            </TableCellHeader>
            <TableCellHeader>
              {intl.formatMessage({ defaultMessage: "ATP/CTP" })}
            </TableCellHeader>
            <TableCellHeader>
              {intl.formatMessage({ defaultMessage: "ATP/CTP Detail" })}
            </TableCellHeader>
            <TableCellHeader>
              {intl.formatMessage({ defaultMessage: "Block" })}
            </TableCellHeader>
            <TableCellHeader>
              {intl.formatMessage({ defaultMessage: "Run" })}
            </TableCellHeader>
            {/* <TableCellHeader className={classes.col_80}>
              {intl.formatMessage({ defaultMessage: "Run" })}
            </TableCellHeader>
            <TableCellHeader className={classes.col_140}>
              {intl.formatMessage({ defaultMessage: "Confirm Date" })}
            </TableCellHeader> */}
          </TableRow>
          {((data?.listItems as ATPCTPListItemsFieldPolicy[]) || [])?.map(
            item => (
              <TableRow key={`${item?.itemNo}`} className={classes.rowData}>
                {displayOrderNo ? (
                  <TableCell className={classes.bottomRow}>
                    <MyTypography variant={"body3"}>
                      {data ? data?.orderNo || "-" : <Skeleton />}
                    </MyTypography>
                  </TableCell>
                ) : null}
                <TableCell className={classes.bottomRow}>
                  <MyTypography variant={"body3"}>
                    {item ? item?.itemNo || "-" : <Skeleton />}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.bottomRow}>
                  <MyTypography variant="body3">
                    {data ? data?.materialVariantCode || "-" : <Skeleton />}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.bottomRow}>
                  <MyTypography variant="body3">
                    {data ? (
                      data?.materialVariantDescription || "-"
                    ) : (
                      <Skeleton />
                    )}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.bottomRow}>
                  <MyTypography
                    style={{
                      color:
                        Number(item?.quantity) !==
                          Number(data?.originalQuantity) && "#FF2600"
                    }}
                    variant={"body3"}
                  >
                    {item ? item?.quantity || "0" : <Skeleton />}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.bottomRow}>
                  <MyTypography
                    style={{
                      color:
                        !moment(`${item?.confirmDate}`).isSame(
                          moment(`${data?.originalRequestDate}`)
                        ) && "#FF2600"
                    }}
                    variant={"body3"}
                  >
                    {item ? (
                      item?.confirmDate ? (
                        convertDate(item?.confirmDate)
                      ) : (
                        "-"
                      )
                    ) : (
                      <Skeleton />
                    )}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.bottomRow}>
                  <MyTypography variant="body3">
                    {item ? item?.plant || "-" : <Skeleton />}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.bottomRow}>
                  <MyTypography variant="body3">
                    {item ? item?.atpCtp || "-" : <Skeleton />}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.bottomRow}>
                  <MyTypography variant="body3">
                    {item ? item?.atpCtpDetail || "-" : <Skeleton />}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.bottomRow}>
                  <MyTypography variant="body3">
                    {item ? item?.blockCode || "-" : <Skeleton />}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.bottomRow}>
                  <MyTypography variant="body3">
                    {item ? item?.runCode || "-" : <Skeleton />}
                  </MyTypography>
                </TableCell>
              </TableRow>
            )
          )}
        </TableHead>
      </Table>
      <StickyHorizontalScrollBar container={containerRef} />
    </div>
  );
};

export default AtpCtpTableData;
